"use client";

import React from "react";
import { v4 as uuidv4 } from "uuid";
import {
  RT_DEMO_CAL_URL,
  RT_FOOTER_BG,
  RT_FOOTER_BG_MOBILE,
  RT_FOOTER_NEW,
  RT_FOOTER_BANNER_MOBILE,
  RT_LOGO_URL,
  RT_FOOTER_BANNER,
} from "@/data";
import { footerData } from "@/data/footerData";
import { MemoizedStars } from "@/components/ui/stars";
import Logo from "@/components/ratl/utility/logo-nav";
import { useRouter } from "next/navigation";
import { Badge } from "@/components/sections/PricingBody";
import CustomButton from "@/components/ratl/utility/CustonButton";
import { useFloat } from "@/lib";
import { FlipWords } from "@/components/ui/flip-words";
import Picture from "@/components/ratl/utility/Picture";
import OnlineStatusIndicator from "@/components/ui/status-indicator";
import BookADemo from "@/components/ui/book-a-demo";
import Image from "next/image";
import PoweredByModels from "./PoweredByModels";
import MobileExploreLinks from "./MobileExploreLinks";
import FooterLinks from "./FooterLinks";
import SocialLinks from "./SocialLinks";

const Footer: React.FC = () => {
  const { items, socials, ratlfooterlinks } = footerData;
  const router = useRouter();
  const { float, handleFloat } = useFloat([false, false]);
  const words = ["Mumbai", "Bengaluru", "Dubai", "San Jose"];

  return (
    <footer
      className="w-full bg-background-2 flex flex-col mt-20 md:mt-0"
      aria-label="Site Footer"
      role="contentinfo"
    >
      {/* Top Section: Call-to-action */}
      <section className="flex flex-col items-center justify-center bg-background-2 text-center px-6 gap-10 mb-20 md:mb-30 md:p-[120px] relative md:my-[4rem]" aria-labelledby="footer-cta-heading">
        <div
          className="px-5 w-full md:w-[1270px] md:h-[449px] absolute md:top-12 left-1/2 -translate-x-1/2 flex justify-center items-center pointer-events-none"
          aria-hidden="true"
        >
          <Image
            src={RT_FOOTER_BANNER}
            alt="Footer Banner"
            width={1270}
            height={449}
            className="object-cover w-[1270px] md:block hidden h-[449px] rounded-[20px]"
            priority
          />
          <Image
            src={RT_FOOTER_BANNER_MOBILE}
            alt="Footer Banner Mobile"
            width={800}
            height={438}
            className="object-cover h-[438px] w-full md:hidden block rounded-[20px]"
            priority
          />
        </div>
        <Badge
          type="Let’s Start testing"
          arrow={true}
          className="text-light-body-text bg-transparent text-[16px] z-20 border-light-sub-heading"
        />
        <div className="flex flex-col gap-4 justify-center items-center z-20">
          <h1 id="footer-cta-heading" className="text-2xl md:text-[40px] leading-[130%] md:font-semibold">
            AI-powered platform to <br />
            <span className="text-2xl md:text-[40px] font-normal md:font-semibold">
              revolutionize testing and reliability
            </span>
          </h1>
          <h2 className="text-[18px] md:text-xl text-light-main-heading max-w-3xl leading-[130%] font-inter">
            Smarter testing, <br className="md:hidden" /> stronger products.
            ratl.ai empowers <br className="md:hidden" /> teams with AI-driven{" "}
            <br className="md:block hidden" /> reliability and{" "}
            <br className="md:hidden" />
            seamless innovation.
          </h2>
        </div>
        <div className="flex flex-col md:flex-col gap-2 justify-center items-center z-20">
          <BookADemo />
          <a
            href="/newsletter"
            className="hover:underline font-normal hover:font-semibold"
            tabIndex={0}
            aria-label="Newsletter"
            role="link"
          >
            Join the Newsletter
          </a>
        </div>
      </section>
      {/* Middle Section: Content with background images and stars */}
      <section className="flex flex-col items-center bg-background-2 text-white md:pt-[120px] md:pb-10 " aria-labelledby="footer-main-content">
        <div className="flex flex-col items-center px-4 py-6 pt-20 md:pt-0 max-w-3xl text-center">
          <Logo logoSrc={RT_LOGO_URL} width={165} />
          <p className="text-base opacity-80 my-4 font-inter text-rt-grey-t1">
            Empower your testing with precision and speed. Welcome to the future
            of automated testing with ratl.ai, where efficiency meets
            intelligence.
          </p>
          <PoweredByModels />
          {/* Mobile Explore Section */}
          <MobileExploreLinks />
        </div>
        <div className="bg-background-2 w-full px-5 md:px-[120px] text-sm flex flex-col md:flex-col justify-center md:justify-between items-center pb-4">
          <div className="w-full" aria-hidden="true">
            <Image
              src={RT_FOOTER_NEW}
              alt="Footer Background"
              width={1200}
              height={200}
              className="transition ease-in duration-200 hidden md:block w-full"
            />
          </div>
          <div className="flex flex-col-reverse md:flex-row border-t border-light-sub-heading/30 justify-center md:justify-between items-center w-full gap-4">
            <FooterLinks words={words} />
            <SocialLinks socials={socials} />
          </div>
        </div>
      </section>
    </footer>
  );
};

export default Footer;
